package com.isaias.fyattravel.web.layout

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.auth.AuthManager
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import com.isaias.fyattravel.web.auth.rememberAuthState
import com.isaias.fyattravel.web.theme.ThemeColors

/**
 * Sidebar navigation component with slide animation, SVG icons, and mobile hamburger menu
 */
@Composable
fun Sidebar(
    route: String,
    onNavigate: (String) -> Unit,
) {
    val authState = rememberAuthState()
    val user = authState.user
    var isExpanded by remember { mutableStateOf(false) }
    var isMobileMenuOpen by remember { mutableStateOf(false) }

    // Mobile top bar (visible only on small screens via CSS media query)
    Div({
        classes(ResponsiveStyles.sidebarTopBar)
        onClick { isMobileMenuOpen = true }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(0.6.em)
            }
        }) {
            Img(src = "icon_fyat.jpg", attrs = {
                style {
                    width(32.px)
                    height(32.px)
                    borderRadius(6.px)
                    property("object-fit", "contain")
                }
            })
        }
        // Hamburger icon SVG
        Span({
            ref { el ->
                el.innerHTML = """
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${ThemeColors.primary}" stroke-width="2">
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <line x1="3" y1="12" x2="21" y2="12"/>
                        <line x1="3" y1="18" x2="21" y2="18"/>
                    </svg>
                """
                onDispose {}
            }
        })
    }

    // Desktop Sidebar
    Div({
        classes(ResponsiveStyles.sidebarDesktop)
        style {
            width(if (isExpanded) 240.px else 62.px)
            minWidth(if (isExpanded) 240.px else 62.px)
            height(100.percent)
            backgroundColor(ThemeColors.surface)
            paddingTop(1.5.em)
            paddingBottom(1.5.em)
            paddingLeft(if (isExpanded) 1.em else 0.75.em)
            paddingRight(if (isExpanded) 1.em else 0.75.em)
            property("border-right", "1px solid ${ThemeColors.divider}")
            property("transition", "width 0.28s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.28s cubic-bezier(0.4, 0, 0.2, 1)")
            property("overflow-x", "hidden")
            property("overflow-y", "auto")
            property("z-index", "10")
            property("flex-shrink", "0")
            // Icon rail on mobile
        }
        onMouseEnter { isExpanded = true }
        onMouseLeave { isExpanded = false }
    }) {
        SidebarContent(route, isExpanded, onNavigate, authState, user)
    }

    // Mobile Overlay Menu
    if (isMobileMenuOpen) {
        // Backdrop
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                right(0.px)
                bottom(0.px)
                backgroundColor(ThemeColors.shadow)
                property("z-index", "40")
            }
            onClick { isMobileMenuOpen = false }
        }) {}

        // Mobile menu panel
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(280.px)
                height(100.percent)
                backgroundColor(ThemeColors.surface)
                property("z-index", "50")
                property("box-shadow", "4px 0 15px rgba(0,0,0,0.1)")
                padding(1.25.em)
                property("@media (max-width: 420px)", "{ width: 85vw; }")
            }
        }) {
            // Close button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(2.em)
                }
            }) {
                // Logo — click to go to the home page
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(0.75.em)
                        cursor("pointer")
                    }
                    onClick {
                        onNavigate("/flights")
                        isMobileMenuOpen = false
                    }
                }) {
                    Img(src = "icon_fyat.jpg", attrs = {
                        style {
                            width(46.px)
                            height(46.px)
                            borderRadius(8.px)
                        }
                    })
                    Div {
                        Div({
                            style {
                                fontWeight("800")
                                color(ThemeColors.textPrimary)
                                fontSize(1.em)
                            }
                        }) { Text(WebStrings.t(WebStringKey.FYAT_TRAVEL_TOUR)) }
                    }
                }

                // Close button
                Div({
                    style {
                        cursor("pointer")
                        padding(0.5.em)
                    }
                    onClick { isMobileMenuOpen = false }
                }) {
                    Span({
                        ref { el ->
                            el.innerHTML = """
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${ThemeColors.textMuted}" stroke-width="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            """
                            onDispose {}
                        }
                    })
                }
            }

            // Mobile menu items
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(0.5.em)
                }
            }) {
                listOf(
                    WebStrings.t(WebStringKey.CATEGORIES_LABEL) to "/categories",
                    WebStrings.t(WebStringKey.EXPLORE_FLIGHTS) to "/flights",
                    WebStrings.t(WebStringKey.EXPLORE_STAYS) to "/stays",
                    WebStrings.t(WebStringKey.CAR_RENTAL) to "/cars",
                    WebStrings.t(WebStringKey.EXPLORE_VISA) to "/visa_logo",
                    WebStrings.t(WebStringKey.EXPLORE_LABEL) to "/explore",
                    WebStrings.t(WebStringKey.CONTACT_US) to "/contact"
                ).forEach { (label, path) ->
                    Div({
                        style {
                            padding(1.em)
                            borderRadius(0.75.em)
                            backgroundColor(if (route == path) ThemeColors.primaryMuted else Color.transparent)
                            color(if (route == path) ThemeColors.primary else ThemeColors.textMuted)
                            fontWeight(if (route == path) "700" else "500")
                            cursor("pointer")
                        }
                        onClick {
                            onNavigate(path)
                            isMobileMenuOpen = false
                        }
                    }) {
                        Text(label)
                    }
                }
            }
        }
    }
}

@Composable
private fun SidebarContent(
    route: String,
    isExpanded: Boolean,
    onNavigate: (String) -> Unit,
    authState: com.isaias.fyattravel.web.auth.AuthState,
    user: com.isaias.fyattravel.web.auth.WebUser?
) {
    // Logo area — click to go to the home page
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.75.em)
            marginBottom(1.5.em)
            paddingBottom(1.em)
            property("border-bottom", "1px solid ${ThemeColors.surface}")
            cursor("pointer")
        }
        onClick { onNavigate("/flights") }
    }) {
        // Logo image
        Img(src = "icon_fyat.jpg", attrs = {
            style {
                width(41.px)
                height(41.px)
                minWidth(41.px)
                borderRadius(8.px)
                property("object-fit", "contain")
            }
        })

        if (isExpanded) {
            Div {
                Div({
                    style {
                        fontWeight("800")
                                color(ThemeColors.textPrimary)
                        fontSize(0.95.em)
                        whiteSpace("nowrap")
                    }
                }) { Text(WebStrings.t(WebStringKey.FYAT_TRAVEL_TOUR)) }
                Div({
                    style {
                        fontSize(0.72.em)
                        color(ThemeColors.textSecondary)
                        whiteSpace("nowrap")
                    }
                }) { Text(WebStrings.t(WebStringKey.TRAVEL_OPERATOR)) }
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(2, auto)")
                        columnGap(0.6.em)
                        rowGap(0.1.em)
                        marginTop(0.2.em)
                        fontSize(0.68.em)
                    }
                }) {
                    listOf(
                        "Uganda" to true,
                        "Rwanda" to false,
                        "Kenya" to false,
                        "South Sudan" to false,
                        "Angola" to false
                    ).forEach { (country, highlighted) ->
                        Div({
                            style {
                                whiteSpace("nowrap")
                                if (highlighted) {
                                    fontWeight("800")
                                    color(ThemeColors.primary)
                                } else {
                                    fontWeight("400")
                                    color(ThemeColors.textSecondary)
                                }
                            }
                        }) { Text(country) }
                    }
                }
            }
        }
    }

    // Navigation sections - no Home, Categories first
    NavItem(
        "",
        WebStrings.t(WebStringKey.CATEGORIES_LABEL),
        active = route == "/categories" || route.startsWith("/category/"),
        isExpanded = isExpanded,
        svgPath = SvgIcons.CATEGORIES
    ) {
        onNavigate("/categories")
    }

    SidebarDivider(isExpanded)

    NavItem("", WebStrings.t(WebStringKey.EXPLORE_FLIGHTS), active = route == "/flights", isExpanded = isExpanded, svgPath = SvgIcons.FLIGHTS) { onNavigate("/flights") }
    NavItem("", WebStrings.t(WebStringKey.EXPLORE_STAYS), active = route == "/stays", isExpanded = isExpanded, svgPath = SvgIcons.HOTEL) { onNavigate("/stays") }
    NavItem("", WebStrings.t(WebStringKey.CAR_RENTAL), active = route == "/cars", isExpanded = isExpanded, svgPath = SvgIcons.CAR) { onNavigate("/cars") }
    NavItem("", WebStrings.t(WebStringKey.EXPLORE_VISA), active = route == "/visa_logo", isExpanded = isExpanded, svgPath = SvgIcons.VISA) { onNavigate("/visa_logo") }

    SidebarDivider(isExpanded)

    NavItem("", WebStrings.t(WebStringKey.EXPLORE_LABEL), active = route == "/explore", isExpanded = isExpanded, svgPath = SvgIcons.EXPLORE) { onNavigate("/explore") }

    // Profile icon - only shown when logged in (Sign In hidden for now)
    if (authState.isAuthenticated && user != null) {
        val profileLabel = user.displayName?.split(" ")?.firstOrNull() ?: WebStrings.t(WebStringKey.PROFILE)
        NavItem("", profileLabel, active = route == "/profile", isExpanded = isExpanded, svgPath = SvgIcons.PROFILE) {
            onNavigate("/profile")
        }
        SidebarDivider(isExpanded)
    }

    NavItem("", WebStrings.t(WebStringKey.CONTACT_US), active = route == "/contact", isExpanded = isExpanded, svgPath = SvgIcons.CONTACT) { onNavigate("/contact") }
}

@Composable
private fun SidebarDivider(isExpanded: Boolean) {
    Hr({
        style {
            margin(if (isExpanded) 0.75.em else 0.5.em, 0.em)
            property("border", "none")
            property("border-top", "1px solid ${ThemeColors.surface}")
        }
    })
}
